import { useState, useEffect, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { userService, setAuthFailureCallback } from '../services/api';
import { clearTokens, setTokens } from '../services/tokenStorage';
import { clearChatToken } from '../services/goChatApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    setAuthFailureCallback(() => {
      clearTokens();
      clearChatToken();
      setUser(null);
      // Drop any per-user data the previous session populated, so the next
      // login doesn't render stale cache (e.g. ['availableCommunities']
      // returned [] for the previous member, hiding it for the new user).
      queryClient.clear();
      // Don't bounce visitors away from public routes — `/` and `/landing` render
      // <Landing /> for unauth users; the rest are explicit public routes.
      const PUBLIC_PATHS = new Set(['/', '/landing', '/login', '/register', '/verify-email', '/invitation', '/join']);
      if (!PUBLIC_PATHS.has(window.location.pathname)) {
        window.location.assign('/login');
      }
    });
  }, [queryClient]);

  // Access token lives in memory only, so a page reload always starts tokenless.
  // Always attempt fetchUser(): the axios 401 interceptor will mint a new access token
  // via the HttpOnly refresh cookie before failing.
  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await userService.getMe();
      const userData = response.data;

      if (userData && userData.id) {
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Auth errors (401/403) are handled by the axios interceptor in api.js.
      // Silently ignore other errors (network failures, 500s, etc.)
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Refresh token is set as an HttpOnly cookie by the backend on /auth/login and /auth/google/*.
  // Caller only needs to pass the access token; the cookie travels via Set-Cookie.
  const login = async (accessToken /* refreshToken: handled by HttpOnly cookie */) => {
    if (!accessToken) {
      throw new Error('Missing access token');
    }

    // Wipe any data cached for the previous user before fetching fresh state.
    queryClient.clear();
    // El chat token (JWT del servicio Go) está taggeado al userId previo —
    // si no lo dropeamos, el nuevo user heredaría la identidad del anterior
    // en cualquier request al chat (Go autoriza con el JWT, no con cookies).
    clearChatToken();
    setTokens(accessToken);
    await fetchUser();
  };

  const logout = async () => {
    try {
      // Call backend so it clears the HttpOnly refresh cookie too.
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (_) { /* ignore — local cleanup still happens below */ }
    clearTokens();
    clearChatToken();
    setUser(null);
    queryClient.clear();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refetchUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
